import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout, SupportIcon } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import {
  useOperationSSE,
  upsertOperationInList,
} from "../hooks/useOperationSSE";
import { ConsumerSessionMenu } from "../components/ConsumerSessionMenu";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { listOperations } from "../services/api";
import type { IOperationResponse } from "../services/api";
import type { ITransaction } from "../interfaces/transactions";
import { APP_ROUTES } from "../constants";
import { usablePersonName } from "../utils/homeGreeting";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { amountFormatter } from "../utils";
import { hasOfacDisambiguation } from "../utils/operationStatus";
import { getEvents, safeCapture } from "../analytics";
import { NotificationsBell } from "../components/notifications/NotificationsBell";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { Navbar } from "../components/Navbar";

/** How many operations are fetched per page as the user scrolls down. */
const PAGE_SIZE = 8;

/** Pull distance (px) required to trigger a refresh on mobile. */
const PULL_THRESHOLD = 70;

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

/**
 * Home screen of the payout-claim flow.
 *
 * Lists the recipient's money-transfer operations (most recent first) with infinite
 * scroll. Reached when the user defers a claim ("Later") and after bind creates
 * an operation.
 *
 * Cash-deposit pending is NOT actionable here: only the sender can complete the
 * in-person deposit (deferred/send). Claim shows those ops as normal status rows.
 */
export const TransactionsPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transactions" }}>
    <TransactionsPageContent />
  </RequireActiveClerkSession>
));

const TransactionsPageContent = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("home"));
  }, []);

  const colorConfig = theme.whiteView;
  const inbox = useNovuInbox();
  const headerSlot = inbox?.headerSlot ?? "help";
  const showBell = headerSlot === "bell";
  const showHelp = headerSlot === "help";
  const { openSupport, supportSheet } = useSupportSheet();

  const senderName =
    usablePersonName(
      typeof session.data?.config?.senderName === "string"
        ? session.data.config.senderName
        : undefined
    ) || undefined;

  // Raw operations are kept (rather than pre-mapped transactions) so that live
  // SSE deltas can be applied by operationId and the derived rows recomputed.
  const [operations, setOperations] = useState<IOperationResponse[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const mapOperation = useCallback(
    (op: IOperationResponse): ITransaction => {
      const needsOfac = hasOfacDisambiguation(op);
      return {
        operationId: op.operationId,
        status: op.status,
        amount: amountFormatter(op.receiveAmount, op.destinationCurrency),
        currency: op.destinationCurrency,
        date: formatDate(op.createdAt),
        actionRequired: op.actionRequired ?? null,
        senderName,
        onSelect: () =>
          redirectTo(
            needsOfac
              ? `${APP_ROUTES.OFAC_DISAMBIGUATION_PAGE}/${op.operationId}`
              : `${APP_ROUTES.RECEIPT_PAGE}/${op.operationId}`
          ),
      };
    },
    [redirectTo, senderName]
  );

  const transactions = useMemo<ITransaction[]>(
    () => operations.map(mapOperation),
    [operations, mapOperation]
  );

  const loadNextPage = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId || loadingRef.current) return;

    loadingRef.current = true;
    const nextPage = pageRef.current + 1;

    try {
      const { items, pagination } = await listOperations(linkId, {
        page: nextPage,
        limit: PAGE_SIZE,
      });

      setOperations((prev) => (nextPage === 1 ? items : [...prev, ...items]));
      pageRef.current = nextPage;
      setHasMore(pagination ? pagination.hasMore : false);
    } catch (error) {
      console.error("[TransactionsPage] Failed to fetch operations:", error);
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
  }, [session.data?.linkId]);

  // Reset to the first page (used by pull-to-refresh).
  const refresh = useCallback(async () => {
    pageRef.current = 0;
    setHasMore(true);
    await loadNextPage();
  }, [loadNextPage]);

  // Apply a live SSE delta to a single operation (status/funding changes).
  const upsertOperation = useCallback((op: IOperationResponse) => {
    setOperations((prev) => upsertOperationInList(prev, op));
  }, []);

  // After an SSE reconnection, re-fetch the most recent page and merge it so we
  // catch up on any deltas missed while the socket was down (non-terminal
  // operations are the most recent and live on the first page).
  const reconcileFromServer = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId) return;
    try {
      const { items } = await listOperations(linkId, {
        page: 1,
        limit: PAGE_SIZE,
      });
      setOperations((prev) =>
        items.reduce((acc, op) => upsertOperationInList(acc, op), prev)
      );
    } catch (error) {
      console.error(
        "[TransactionsPage] SSE reconnect reconcile failed:",
        error
      );
    }
  }, [session.data?.linkId]);

  // Keep operation statuses live while any loaded operation is non-terminal.
  useOperationSSE(
    session.data?.linkId,
    operations,
    upsertOperation,
    reconcileFromServer
  );

  const { pullDistance, refreshing, pulling } = usePullToRefresh(
    containerRef,
    refresh,
    { threshold: PULL_THRESHOLD }
  );

  // Initial page once the session (linkId) is available.
  useEffect(() => {
    if (session.data?.linkId && pageRef.current === 0) {
      void loadNextPage();
    }
  }, [session.data?.linkId, loadNextPage]);

  // Infinite scroll: load the next page when the bottom sentinel is revealed.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadNextPage();
      },
      { root: containerRef.current, rootMargin: "120px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, transactions.length, loadNextPage]);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={
          showBell
            ? "need-help-header notifications-home-header"
            : "need-help-header"
        }
      >
        <Layout.Header>
          <ConsumerSessionMenu />
          {headerSlot === "pending" ? null : (
            <Navbar
              align="right"
              noBackButton
              title={showHelp ? t("common.needHelp") : undefined}
              onIconClick={showHelp ? () => openSupport() : undefined}
              icon={
                showBell ? (
                  <NotificationsBell />
                ) : (
                  <SupportIcon size={35} colors={colorConfig.dropzoneColors} />
                )
              }
            />
          )}
        </Layout.Header>
        <Layout.Content className="transactions-section">
          <TransactionsHistory
            items={transactions}
            containerRef={containerRef}
            sentinelRef={sentinelRef}
            hasMore={hasMore}
            pullDistance={pullDistance}
            refreshing={refreshing}
            pulling={pulling}
            pullThreshold={PULL_THRESHOLD}
          />
        </Layout.Content>
      </AuraLayout>
      {showHelp ? supportSheet : null}
    </>
  );
});
